import config from '../config';
import get from 'lodash/get';
import pick from 'lodash/pick';
import { CognitoState } from 'react-cognito';
import { loginStabilized } from '../lib/react';
import { invokeApi } from './api';
import { buildAction } from './helpers';
import {
  EMPTY_OBJECT,
  PLAN,
  SET_CHOSEN_PLAN,
  SET_ACTIVE_PLAN,
  SET_COUPON_CODE,
  SET_COUPON_ERROR,
  SET_PAYMENT_ERROR,
  SET_CHOSEN_SUMMARY,
  SET_SUBSCRIPTION,
} from '../constants';

const PLANS = {
  [PLAN.BASIC]: {
    TITLE: 'Starter',
    MONTHLY: { price: 49, id: 'starter_monthly_11-15-17' },
    ANNUAL: { price: 29, id: 'starter_annual_11-15-17' },
  },
  [PLAN.PRO]: {
    TITLE: 'Basic',
    MONTHLY: { price: 99, id: 'basic_monthly_11-15-17' },
    ANNUAL: { price: 79, id: 'basic_annual_11-15-17' },
  },
  [PLAN.UNLIMITED]: {
    TITLE: 'Unlimited',
    MONTHLY: { price: 249, id: 'unlimited_monthly' },
    ANNUAL: { price: 199, id: 'unlimited_annual' },
  },
  [PLAN.WEBINAR]: {
    TITLE: 'Unlimited',
    MONTHLY: { price: 199, id: 'unlimited_monthly_reduced' },
    ANNUAL: { price: 149, id: 'unlimited_annual_reduced' },
  },
};

export function getNucleusUpgradeData() {
  return function(dispatch) {
    return dispatch(
      invokeApi({
        path: '/transition/data',
        method: 'POST',
        endpoint: config.api.mediaUrl,
      })
    );
  };
}

export function getPlanTitle(name) {
  return get(PLANS, [name, 'TITLE']);
}

export function getPlanPrice(name, annual) {
  const periodKey = annual ? 'ANNUAL' : 'MONTHLY';
  return get(PLANS, [name, periodKey, 'price']);
}

function getPlanId(name, annual) {
  const periodKey = annual ? 'ANNUAL' : 'MONTHLY';
  return get(PLANS, [name, periodKey, 'id']);
}

function getPlanById(stripePlanId) {
  for (let plan of Object.keys(PLANS)) {
    if (stripePlanId === PLANS[plan].MONTHLY.id) {
      return { name: plan, annual: false };
    } else if (stripePlanId === PLANS[plan].ANNUAL.id) {
      return { name: plan, annual: true };
    }
  }

  return EMPTY_OBJECT;
}

export function setChosenPlan({ name, annual }) {
  return function(dispatch) {
    dispatch(buildAction(SET_CHOSEN_PLAN, planPayload(name, annual)));
    return dispatch(updatePlanSummary());
  };
}

export function setCouponCode(code) {
  return function(dispatch) {
    dispatch(buildAction(SET_COUPON_ERROR, undefined));
    dispatch(buildAction(SET_COUPON_CODE, code));
    return dispatch(updatePlanSummary());
  };
}

export function getActiveSubscription() {
  return function(dispatch, getState) {
    return loginStabilized(getState).then(() => {
      if (getState().cognito.state !== CognitoState.LOGGED_IN) {
        return Promise.resolve(
          dispatch(
            buildAction(SET_ACTIVE_PLAN, {
              plan: undefined,
              status: undefined,
              annual: undefined,
            })
          )
        );
      }

      return dispatch(invokeApi({ path: '/v1/subscriptions' })).then(results =>
        setSubscriptionValues(dispatch, results)
      );
    });
  };
}

export function cancelSubscription() {
  return function(dispatch) {
    return dispatch(
      invokeApi({ path: '/v1/subscriptions', method: 'DELETE' })
    ).then(results => setSubscriptionValues(dispatch, results));
  };
}

export function updatePayment(stripeTokenId) {
  return function(dispatch, getState) {
    const state = getState();
    if (!state.plan.activePlan.name) {
      return Promise.reject('No active plan to update.');
    }

    return dispatch(
      invokeApi({
        path: '/v1/subscriptions',
        method: 'POST',
        body: { stripeToken: stripeTokenId },
      })
    ).then(results => setSubscriptionValues(dispatch, results));
  };
}

export function subscribe(stripeTokenId, plan, coupon) {
  return function(dispatch, getState) {
    const cognitoState = getState().cognito;
    const email = cognitoState.attributes.email || cognitoState.user.username;
    const planId = getPlanId(plan.name, plan.annual);
    const params = {
      email: email,
      stripeToken: stripeTokenId,
      planLevel: plan.name,
      stripePlanId: planId,
    };

    if (coupon) {
      params['couponCode'] = coupon;
    }

    return dispatch(
      invokeApi({
        path: '/v1/subscriptions',
        method: 'POST',
        body: params,
      })
    ).then(subscription => setSubscriptionValues(dispatch, subscription));
  };
}

function setSubscriptionValues(dispatch, subscription) {
  dispatch(
    buildAction(
      SET_ACTIVE_PLAN,
      pick(subscription, ['plan', 'status', 'annual'])
    )
  );
  dispatch(
    buildAction(
      SET_SUBSCRIPTION,
      pick(subscription, [
        'licensesMax',
        'licensesResetAt',
        'licensesUsed',
        'legacy',
        'canceledAfter',
      ])
    )
  );
}

function planPayload(name, annual) {
  const payload = {};

  if (name !== undefined) {
    payload.name = name;
  }

  if (annual !== undefined) {
    payload.annual = annual;
  }

  return payload;
}

function updatePlanSummary() {
  return async function(dispatch, getState) {
    const state = getState();

    const couponCode = state.plan.couponCode;
    const { name, annual } = state.plan.chosenPlan;
    const planId = getPlanId(name, annual);

    if (!planId) {
      return;
    }

    try {
      const summary = await dispatch(getPlanSummary(planId, couponCode));
      dispatch(buildAction(SET_CHOSEN_SUMMARY, summary));
    } catch (err) {
      if (couponCode === undefined) {
        dispatch(buildAction(SET_PAYMENT_ERROR, err.message));
      } else {
        dispatch(buildAction(SET_COUPON_ERROR, err.message));

        try {
          const summary = await dispatch(getPlanSummary(planId));
          dispatch(buildAction(SET_CHOSEN_SUMMARY, summary));
        } catch (err) {
          dispatch(buildAction(SET_PAYMENT_ERROR, err.message));
        }
      }
    }
  };
}

const SUMMARY_CACHE = {};
function getPlanSummary(planId, couponCode) {
  return async function(dispatch) {
    const queryParams = { stripe_plan_id: planId };
    if (couponCode) {
      queryParams['coupon'] = couponCode;
    }

    const cacheKey = `${planId}_${couponCode}`;

    if (!couponCode) {
      // In the case of no coupon we can just use the local info to get the price.
      const planInfo = getPlanById(planId);
      if (planInfo.name) {
        const price = getPlanPrice(planInfo.name, planInfo.annual);
        const title = getPlanTitle(planInfo.name);

        const total = planInfo.annual ? 12 * price : price;

        SUMMARY_CACHE[cacheKey] = {
          name: title,
          total: total,
          monthly: price,
          regularMonthly: price,
          percent_off: null,
          amount_off: null,
          discountPostfix: null,
        };
      }
    }

    if (SUMMARY_CACHE[cacheKey]) {
      return SUMMARY_CACHE[cacheKey];
    }

    SUMMARY_CACHE[cacheKey] = await dispatch(
      invokeApi({
        path: '/v1/subscriptions/cart',
        queryParams: queryParams,
      })
    );

    return SUMMARY_CACHE[cacheKey];
  };
}



// WEBPACK FOOTER //
// ./src/actions/plan.js